import { cn } from './ui';

/* =============================================================================
 * The logo
 * -----------------------------------------------------------------------------
 * An N and a 1 cut out of one block and read as faceted planes: three greens
 * standing for three angles of the same surface, with the ground showing through
 * between them. Underneath, the name, drawn as lettering rather than set in a
 * face the reader's machine might not have.
 *
 * -----------------------------------------------------------------------------
 * IT IS ARTWORK, NOT GEOMETRY. THIS IS THE IMPORTANT PART.
 * -----------------------------------------------------------------------------
 * This file used to *draw* the logo, and so did four other files — an arc, a
 * polyline, a few hand-plotted leaves — each a flat approximation of a mark none
 * of them had. Nobody asked for a redraw; it was simply what could be done in
 * SVG, so it became what shipped, in five versions that drifted apart.
 *
 * So the artwork is the artwork. public/brand/mark.svg, wordmark.svg and the
 * lockup built from them are the logo, and everything that shows it — this
 * file, the PDF letterheads, the icon set, the email logo, the boot screen —
 * reads those files and nothing else.
 *
 * What that costs, stated plainly:
 *
 *   it is not recoloured      the three tones are the drawing. Each is behind a
 *                             custom property so a single screen can retint the
 *                             whole mark, but nothing recolours it by default.
 *   it is one drawing         so a change to the artwork is a change to the
 *                             sidebar, the letterhead and the launcher at once.
 *
 * What it no longer costs is a ground. The old mark was dark and vanished on a
 * dark page, so most screens asked for a chip behind it; this one is mid-green
 * with gaps that go dark with the page, and reads on either. See `chip`.
 *
 * -----------------------------------------------------------------------------
 * The Arabic lockup is still set as text
 * -----------------------------------------------------------------------------
 * Because there is no Arabic artwork. The name is set from BrandName in the
 * app's own face, beside the real mark. If an Arabic lockup is ever rendered to
 * match, drop it in as lockup-ar.svg and this component should read it instead.
 * ========================================================================== */

/**
 * Which colours the *name* takes, where the name is set as text.
 *
 * This no longer touches the mark or the English lockup — both are fixed
 * artwork. It applies to the Arabic lockup and to BrandName used on its own.
 */
export type BrandTone = 'auto' | 'onLight' | 'onDark' | 'onBrand';

/**
 * The two files the logo lives in. Vector, so they are exact at 28 pixels in a
 * header and at 2732 on a splash screen.
 *
 * Paths rather than imports, so replacing the artwork needs no rebuild of
 * anything that shows it — and so the same URLs are what the PDF letterhead and
 * the icon generator read.
 */
export const MARK_SRC = '/brand/mark.svg';
export const LOCKUP_SRC = '/brand/lockup.svg';

/**
 * Width divided by height, as drawn: the block is taller than it is wide, so a
 * square box leaves a gap either side and a stretched one shears the planes.
 *
 * Read off mark.svg's viewBox and checked against it by brand.test.ts, because
 * the mark is fitted rather than stretched — get this wrong and nothing breaks
 * loudly, the logo simply sits badly in its box.
 */
export const MARK_RATIO = 328 / 391;

/* The full lockup's proportions, and how much taller it is than the mark inside
   it — so `size` means the same thing whichever of the two is shown. Both come
   from the generated lockup.svg: 610x528 overall, with the mark 391 tall. */
const LOCKUP_RATIO = 610 / 528;
const LOCKUP_MARK_SCALE = 528 / 391;

/* The brand's own colours, for when the theme cannot be consulted. The three
   greens are the artwork's own tones, sampled from mark.svg: deep is the shaded
   plane, green the body, light the lit edge. There is no gold in this logo. */
const FIXED = {
  onLight: { green: '#5D9350', light: '#80B269', deep: '#306A39' },
  onDark: { green: '#80B269', light: '#A8CE93', deep: '#B7D9A5' },
} as const;

function inkFor(tone: BrandTone) {
  if (tone === 'auto') {
    return { green: 'var(--brand)', light: 'var(--brand)', deep: 'var(--brand-active)' };
  }

  /* Reversed out of a solid brand panel, in one colour. --brand-fg is defined
     per preset per scheme as the colour that reads on --brand, and taking it
     through currentColor means the lockup inherits whatever the panel already
     set for its text, so the two can never disagree. */
  if (tone === 'onBrand') {
    return { green: 'currentColor', light: 'currentColor', deep: 'currentColor' };
  }

  return FIXED[tone];
}

/**
 * Put the artwork on a ground of its own.
 *
 * `true` for the one case that still wants it: a panel painted in the brand's
 * own green, where a green logo on green is no logo at all.
 *
 * `'auto'` is what most screens ask for and it now paints nothing. It was there
 * because the old mark was dark and disappeared on a dark page; this one reads
 * on either, and a white tile behind it looks like a sticker. The rule lives in
 * app.css, so the decision is made once about the page's colours rather than
 * argued screen by screen.
 */
type Chip = boolean | 'auto';

function chipClass(chip: Chip): string | false {
  if (chip === 'auto') return 'brand-chip-auto';
  return chip && 'brand-chip';
}

/* -----------------------------------------------------------------------------
 * The mark on its own
 * -----------------------------------------------------------------------------
 * `size` is its HEIGHT. The width follows from the artwork, because the block is
 * taller than it is wide and a square box would either shear its planes or draw
 * it smaller than was asked for.
 * -------------------------------------------------------------------------- */
export function BrandMark({
  size = 32, chip = false, className,
}: {
  /** The mark's height in pixels. The width follows MARK_RATIO. */
  size?: number;
  chip?: Chip;
  className?: string;
}) {
  const image = (
    <img
      src={MARK_SRC}
      height={size}
      width={Math.round(size * MARK_RATIO)}
      alt=""
      /* Decorative: the name is beside it as real text everywhere this is used,
         and a screen reader announcing the name twice is worse than a mark it
         skips. The lockup below, which *is* the name, is labelled. */
      aria-hidden
      className={cn('shrink-0 select-none object-contain', !chip && className)}
      draggable={false}
    />
  );

  if (!chip) return image;

  /* Proportional padding, so the chip is the same shape at 28 pixels in a
     header and at 64 on a sign-in screen. */
  return (
    <span
      className={cn(
        'shrink-0 inline-grid place-items-center rounded-card',
        chipClass(chip),
        className,
      )}
      style={{ padding: Math.max(3, Math.round(size * 0.14)) }}
    >
      {image}
    </span>
  );
}

/* -----------------------------------------------------------------------------
 * The mark with the name
 * -----------------------------------------------------------------------------
 * In English this is the artwork itself — mark, name and ERP as they were drawn,
 * with the spacing and the gold the design sets. Nothing here re-lays it out,
 * because every part of that arrangement is part of the logo.
 *
 * `stacked` is therefore no longer a layout choice for the English lockup: the
 * artwork is stacked. It survives as a prop because the Arabic lockup, which is
 * assembled here, still has the choice to make.
 * -------------------------------------------------------------------------- */
export function BrandLockup({
  size = 34, tone = 'auto', stacked = false, arabic = false, chip = false, className,
}: {
  /**
   * The height of the MARK within the lockup, so one number moves everything and
   * the value means what it means on BrandMark.
   */
  size?: number;
  tone?: BrandTone;
  stacked?: boolean;
  /** Sets the name in Arabic, assembled from BrandName. See the note above. */
  arabic?: boolean;
  chip?: Chip;
  className?: string;
}) {
  if (arabic) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2.5',
          stacked && 'flex-col gap-2 text-center',
          className,
        )}
      >
        <BrandMark size={size} chip={chip} />
        <BrandName size={size} tone={tone} arabic centred={stacked} />
      </span>
    );
  }

  const height = Math.round(size * LOCKUP_MARK_SCALE);

  const image = (
    <img
      src={LOCKUP_SRC}
      height={height}
      width={Math.round(height * LOCKUP_RATIO)}
      alt="NoorOne ERP"
      className={cn('shrink-0 select-none object-contain', !chip && className)}
      draggable={false}
    />
  );

  if (!chip) return image;

  return (
    <span
      className={cn(
        'shrink-0 inline-grid place-items-center rounded-card',
        chipClass(chip),
        className,
      )}
      style={{ padding: Math.max(4, Math.round(size * 0.12)) }}
    >
      {image}
    </span>
  );
}

/* -----------------------------------------------------------------------------
 * The name, as text
 * -----------------------------------------------------------------------------
 * Two words in two colours and never a space between them: the name is one word
 * written as two, and a space would make it two.
 *
 * In Arabic the two are transliterated with a space between them, because Arabic
 * does not run words together the way the English lockup does. Noor is a word in
 * Arabic before it is a brand, which is the whole reason the name works here.
 *
 * ERP is left as ERP. It is read as three Latin letters across the Gulf, on
 * letterheads and in job titles, and translating it would be accurate and would
 * be the one part of the logo nobody recognised.
 * -------------------------------------------------------------------------- */
export function BrandName({
  size = 34, tone = 'auto', arabic = false, centred = false, className,
}: {
  size?: number;
  tone?: BrandTone;
  arabic?: boolean;
  centred?: boolean;
  className?: string;
}) {
  const ink = inkFor(tone);

  const nameSize = Math.round(size * 0.52);
  const ruleSize = Math.max(7, Math.round(size * 0.2));

  return (
    <span
      className={cn('inline-flex flex-col', centred && 'items-center', className)}
      dir={arabic ? 'rtl' : 'ltr'}
    >
      <span
        className="font-bold leading-none tracking-tight whitespace-nowrap"
        style={{ fontSize: nameSize }}
      >
        {/* One colour, as the drawn wordmark is. It was Noor in green and One
            in gold, which was the old logo's own arrangement — there is no gold
            in this one, and two greens a shade apart would read as a mistake
            rather than as a distinction. */}
        <span style={{ color: ink.deep }}>{arabic ? 'نور' : 'Noor'}</span>
        {arabic && ' '}
        <span style={{ color: ink.deep }}>{arabic ? 'ون' : 'One'}</span>
      </span>

      {/* ERP between two tapered rules. The rules are what stop three loose
          letters reading as an abbreviation somebody forgot to finish. */}
      <span
        className="inline-flex items-center gap-1.5 mt-1 leading-none"
        style={{ fontSize: ruleSize }}
      >
        <span
          aria-hidden="true"
          className="h-px w-4 shrink-0"
          style={{ background: `linear-gradient(to right, transparent, ${ink.light})` }}
        />
        <span className="font-bold tracking-[0.22em]" style={{ color: ink.green }}>
          ERP
        </span>
        <span
          aria-hidden="true"
          className="h-px w-4 shrink-0"
          style={{ background: `linear-gradient(to left, transparent, ${ink.light})` }}
        />
      </span>
    </span>
  );
}
